// ============================================================================
// Re-assign a booking to a different provider by hand (override the router).
// The backend supersedes the old booking — raising an async cancel/confirm task
// for a live one — and creates a fresh booking under the target provider.
// For a MANUAL target, an entered reference brings the new booking live at once;
// leave it blank to have the ops team confirm the booking later from the inbox.
// A live manual booking is also its vehicle allotment (no dispatch webhook ever
// arrives), so the trip — vehicle, driver — is captured here alongside the
// reference rather than being chased afterwards.
// ============================================================================
import { Alert, Form, Input, Modal, Select } from "antd";
import React, { useEffect } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { reassignProvider } from "../logistics.api";
import { trackingUrlRules } from "../porter_link";
import { LogisticsOrderRow, ReassignBody } from "../../types";

interface Props {
  open: boolean;
  order: LogisticsOrderRow | null;
  onClose: () => void;
  onReassigned: () => void; // parent refetches
}

// Targets an operator can move a booking to. Own TMS (automated) books via the carrier API; the
// manual providers surface a booking task; supplier-managed means the supplier moves it (no booking).
const PROVIDER_TARGETS = [
  { value: "own_tms", label: "Own fleet (Loginext) — booked automatically" },
  { value: "porter", label: "Porter — you book it on the Porter app" },
  { value: "intercity", label: "Intercity / part-load — you book it with the partner" },
  { value: "supplier_managed", label: "Supplier delivers — no truck to book" },
];

const MANUAL_TARGETS = new Set(["porter", "intercity"]);

const ReassignModal: React.FC<Props> = ({ open, order, onClose, onReassigned }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const target = Form.useWatch("to_provider", form);
  const isManualTarget = MANUAL_TARGETS.has(target);
  // An entered booking number brings the new booking live immediately, which for a manual carrier is
  // the vehicle allotment — so the vehicle becomes mandatory the moment a reference is typed.
  const hasRef = !!Form.useWatch("provider_ref", form)?.trim();
  // Porter is the one manual carrier we can poll for status, via its tracking link. Optional (unlike
  // the vehicle below): without it this booking is simply closed by hand, as Porter bookings are today.
  const isPorter = target === "porter";

  useEffect(() => {
    if (order) form.resetFields();
  }, [order, form]);

  const handleOk = async () => {
    if (!order) return;
    const values = await form.validateFields();
    const body: ReassignBody = { to_provider: values.to_provider };
    if (isManualTarget) {
      if (values.provider_ref?.trim()) body.provider_ref = values.provider_ref.trim();
      if (values.tracking_url?.trim()) body.tracking_url = values.tracking_url.trim();
      if (values.vehicle_number?.trim()) body.vehicle_number = values.vehicle_number.trim();
      if (values.driver_name?.trim()) body.driver_name = values.driver_name.trim();
      if (values.driver_number?.trim()) body.driver_number = values.driver_number.trim();
    }
    setSaving(true);
    const created = await reassignProvider(order.id, body);
    setSaving(false);
    if (created) {
      onReassigned();
      onClose();
    }
  };

  return (
    <Modal
      title={`Re-assign booking #${order?.id ?? ""}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Re-assign"
      confirmLoading={saving}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="The current booking will be replaced."
          description="If the carrier has already accepted it, we start cancelling it — automatically for our own fleet, or as a task for you to cancel with the partner and confirm. The new booking is created straight away without waiting for that."
        />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Move to which carrier?"
            name="to_provider"
            rules={[{ required: true, message: "Pick a carrier" }]}
          >
            <Select placeholder="Select a carrier" options={PROVIDER_TARGETS} />
          </Form.Item>
          {isManualTarget && (
            <>
              <Form.Item
                label="Carrier's booking number (optional)"
                name="provider_ref"
                extra="Already booked with the partner? Enter their number and this goes live now. Otherwise leave it blank — it will show up in Pending Tasks for you to confirm once you have booked it."
              >
                <Input placeholder="e.g. PORTER-8842" maxLength={255} allowClear />
              </Form.Item>
              <Form.Item
                label={isPorter ? "Porter live-tracking link (optional)" : "Tracking URL (optional)"}
                name="tracking_url"
                rules={trackingUrlRules(isPorter)}
                extra={
                  isPorter
                    ? "The share link from the Porter app — paste it and we'll mark this delivered automatically when Porter completes the trip."
                    : undefined
                }
              >
                <Input placeholder="https://…" allowClear />
              </Form.Item>
              {/* The trip, captured at the moment it is known. A manual carrier sends us no dispatch
                  webhook, so if we don't take the vehicle here it never arrives — the booking goes
                  live and sits on the "needs vehicle" queue. Hence required once a booking number
                  is entered (that is what takes it live); optional otherwise, when the vehicle is
                  still captured later when the Pending Task is confirmed. */}
              <Form.Item
                label={`Vehicle number${hasRef ? "" : " (optional)"}`}
                name="vehicle_number"
                rules={[
                  {
                    required: hasRef,
                    message: "Enter the vehicle — the booking number takes this live now",
                  },
                ]}
                extra={
                  hasRef
                    ? undefined
                    : "Leave blank if you haven't booked yet — you'll enter it when you confirm the task."
                }
              >
                <Input placeholder="e.g. KA01AB1234" maxLength={32} allowClear />
              </Form.Item>
              <Form.Item label="Driver name (optional)" name="driver_name">
                <Input placeholder="e.g. Ramesh" maxLength={128} allowClear />
              </Form.Item>
              <Form.Item label="Driver phone (optional)" name="driver_number">
                <Input placeholder="e.g. 9876543210" maxLength={20} allowClear />
              </Form.Item>
            </>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ReassignModal;
